import React, { useState, useEffect, useCallback, useId, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import cn from "classnames";

import CityStateZipInput from "../landing/cityStateZipInput";
import { actions as landingSearch } from "../../actions/landing";
import { Button } from "../common/button";
import { ReactComponent as SearchIcon } from "../../media/icons/search.svg";
import { ReactComponent as Arrow } from "../../media/icons/arrow-right.svg";
import { Modal } from "../common/modal";
import { actions } from "../../actions/search";
import { heritageUrls, heritageUrlTypes } from "../../constants/main";
import SelectInput from "../common/selectInput";
import styles from "./mainSearch.module.css";

const EMPTY_COMMUNITIES = [];

const MainSearch = ({ className, communities: communitiesList }) => {
  const safeCommunitiesList = Array.isArray(communitiesList) ? communitiesList : EMPTY_COMMUNITIES;
  const [activeCommunitiesModal, setActiveCommunitiesModal] = useState(false);
  const [activeCommunities, setActiveCommunities] = useState({});
  const [activeListingModal, setActiveListingModal] = useState(false);
  const { professions, practices } = useSelector(state => state.search);
  const dispatch = useDispatch();
  const getPractices = useCallback(cityStateZip => dispatch(actions.getPractices(cityStateZip)), [dispatch]);
  const getProfessionsTree = useCallback(() => dispatch(actions.getProfessionsTree()), [dispatch]);
  const [cityStateZipValue, setCityStateZipValue] = useState("");
  const [activeCityStateZip, setActiveCityStateZip] = useState({});
  const getCityStateZip = useCallback(cityStateZip => dispatch(landingSearch.getCityStateZip(cityStateZip)), [dispatch]);
  const { cityStateZipList } = useSelector(state => state.landing);
  const [activeProfession, setActiveProfession] = useState({});
  const [activePractices, setActivePractices] = useState({});
  const languageValue = activeCommunities?.communityType?.includes("language") ? activeCommunities?.heritage_title : "";

  const allCommunityValue = useMemo(() => {
    const globalCommunity = safeCommunitiesList.filter(item => item.heritage_group_title.toLowerCase() === "global");
    if (globalCommunity?.[0]?.children?.[0]) {
      return globalCommunity?.[0]?.children?.[0];
    }

    return {};
  }, [safeCommunitiesList]);

  const handleOpenCommunities = () => {
    setActiveCommunitiesModal(true);
  };

  const handleCloseCommunities = () => {
    setActiveCommunitiesModal(false);
  };

  const handleSelectCommunities = community => {
    setActiveCommunities(community);
    handleCloseCommunities();
  };

  const redirectUrl = useCallback(url => {
    if (window.location.host.includes(heritageUrlTypes.localhost)) {
      // REACT_APP_DIRECTORY_URL: a PR review slot's own directory site (pr-local.sh moves it to the slot's port).
      window.open(`${process.env.REACT_APP_DIRECTORY_URL || heritageUrls.localhost}${url}`, "_blank");
    } else if (window.location.host.includes(heritageUrlTypes.staging) || window.location.host.includes(heritageUrlTypes.qa)) {
      window.open(`${heritageUrls.staging}${url}`, "_blank");
    } else {
      window.open(`${heritageUrls.production}${url}`, "_blank");
    }
  }, []);

  const handleSubmit = () => {
    const state = activeCityStateZip.state || "";
    const city = activeCityStateZip.city || "";
    const country = activeCityStateZip.country_code?.toLowerCase() || "us";

    function buildUrl({ community, profession, specialty, language }) {
      const countryCode = country ? country.replaceAll(" ", "-") : "us";
      const communityCode = community || "all";
      const stateCode = state ? state.replaceAll(" ", "-") : "";
      const cityCode = city ? city.replaceAll(" ", "-") : "";

      const locationParts = [countryCode, stateCode, cityCode]
        .filter(Boolean)
        .map(item => item.toLowerCase())
        .join("/");
      const otherParts = [profession, specialty, communityCode]
        .filter(Boolean)
        .map(item => item.toLowerCase())
        .join("-");
      const languageQuery = language ? `?language=${language}` : "";

      return `/${locationParts}-${otherParts}${languageQuery}`;
    }

    const mainUrl = buildUrl({
      community: activeCommunities.communityType?.includes("language") ? allCommunityValue.heritage_path : activeCommunities.heritage_path,
      profession: activeProfession.title ? activeProfession.title?.toLowerCase() : "",
      specialty: activePractices.title ? activePractices.title.toLowerCase()?.replaceAll(" ", "-") : "",
      language: languageValue
    });

    redirectUrl(mainUrl);
  };

  // const handleOpenProfessionModal = () => {
  //   setActiveListingModal(true);
  // };

  const handleCloseProfessionModal = () => {
    setActiveListingModal(false);
  };

  useEffect(() => {
    getPractices();
    getProfessionsTree();
  }, []);

  // const activeCategory = useMemo(() => {
  //   return activePractices.title || activeProfession.title || "Select Category";
  // }, [activeProfession.title, activePractices.title]);

  return (
    <>
      <div className={cn(styles.main, className)}>
        <div className={styles.flex}>
          <div className={styles.inputWrap}>
            <CityStateZipInput
              className={styles.input}
              cityStateZipList={cityStateZipList}
              setCityStateZipValue={setCityStateZipValue}
              cityStateZipValue={cityStateZipValue}
              getCityStateZip={getCityStateZip}
              setActiveCityStateZip={setActiveCityStateZip}
              placeholder="Search location"
            />
            <SearchIcon className={styles.searchIcon} />
          </div>

          <div className={styles.communitySelectWrap}>
            <button type="button" className={styles.communitySelect} onClick={handleOpenCommunities}>
              {activeCommunities.heritage_title || "Select Community"}
              <Arrow />
            </button>

            {/* <button type="button" className={styles.selectCategory} onClick={handleOpenProfessionModal}> */}
            {/*  {activeCategory} */}
            {/*  <ArrowDown /> */}
            {/* </button> */}
          </div>

          <Button className={styles.button} onClick={handleSubmit}>
            Search
          </Button>
        </div>
      </div>

      {activeCommunitiesModal && (
        <CommunitiesModal
          activeCommunities={activeCommunities}
          handleSelectCommunities={handleSelectCommunities}
          closeModal={handleCloseCommunities}
          communities={safeCommunitiesList.filter(item => item.heritage_group_title.toLowerCase() !== "global")}
          allCommunityValue={allCommunityValue}
        />
      )}

      {activeListingModal && (
        <ProfessionsModal
          setActivePracticesParent={setActivePractices}
          setActiveProfessionParent={setActiveProfession}
          closeModal={handleCloseProfessionModal}
          professions={professions}
          practices={practices}
        />
      )}
    </>
  );
};

const ProfessionsModal = ({ closeModal, professions, setActiveProfessionParent, setActivePracticesParent }) => {
  const [searchPracticesValue, setSearchPracticesValue] = useState("");
  const [practices, setPractices] = useState([]);
  const [activeProfession, setActiveProfession] = useState({});
  const [activePractices, setActivePractices] = useState({});

  const handleClickActiveProfession = professionId => {
    const profession = professions.find(item => String(item.profession_id) === String(professionId));

    setActiveProfessionParent(profession);
    setActiveProfession(profession);
    setPractices(profession.children);
    setSearchPracticesValue("");
  };

  const handleClosePracticeModal = () => {
    closeModal();
  };

  const handleSelectPracticeAndCloseModal = practice => {
    setActivePracticesParent(practice);
    setActivePractices({});
    setActiveProfession({});
    closeModal();
  };

  const handleSearchPractices = value => {
    const findPractices = value
      ? activeProfession.children.filter(item => {
          return item.title.toLowerCase().includes(value.toLowerCase());
        })
      : [];

    setActivePracticesParent(findPractices);
    setPractices(findPractices);
    setSearchPracticesValue(value);
  };

  const handleSelectParentPractice = practice => {
    if (practice.children.length) {
      setActivePractices(practice);
    } else {
      handleSelectPracticeAndCloseModal(practice);
    }
  };

  return (
    <Modal closeModal={closeModal} clearAllModals={closeModal} classNameMain={styles.modal}>
      {!activeProfession.title && (
        <>
          <div className={cn(styles.inputWrap, styles.inputWrapModal)}>
            <SearchIcon className={styles.searchIcon} />
            <input
              type="text"
              className={cn(styles.input, styles.inputModal)}
              placeholder="Search by Industry"
              value={searchPracticesValue}
              onChange={event => handleSearchPractices(event.target.value)}
            />
          </div>

          {professions.map(item => {
            return (
              <button
                type="button"
                key={item.title}
                className={styles.categoryItemProfession}
                onClick={() => handleClickActiveProfession(item.profession_id)}
              >
                {item.title}
              </button>
            );
          })}
        </>
      )}

      {activeProfession.title && (
        <>
          {!activePractices.title ? (
            <>
              <div className={styles.heading}>
                <button type="button" className={cn(styles.viewAll, styles.back)} onClick={() => setActiveProfession({})}>
                  <Arrow />
                  Back
                </button>

                <button type="button" className={cn(styles.viewAll, styles.back)} onClick={handleClosePracticeModal}>
                  Select {activeProfession.title}
                </button>
              </div>

              <div className={cn(styles.inputWrap, styles.inputWrapModal)}>
                <SearchIcon className={styles.searchIcon} />
                <input
                  type="text"
                  className={cn(styles.input, styles.inputModal)}
                  placeholder="Search by Practices"
                  value={searchPracticesValue}
                  onChange={event => handleSearchPractices(event.target.value)}
                />
              </div>

              <div className={styles.categories}>
                <div className={cn(styles.category, styles.practicesContent)}>
                  {practices.map(item => {
                    return (
                      <>
                        <button
                          key={item.title}
                          type="button"
                          className={styles.categoryItemPractices}
                          onClick={() => handleSelectParentPractice(item)}
                        >
                          {item.title}
                        </button>
                      </>
                    );
                  })}
                </div>
              </div>
            </>
          ) : (
            <div>
              <div className={styles.heading}>
                <button type="button" className={cn(styles.viewAll, styles.back)} onClick={() => setActivePractices({})}>
                  <Arrow />
                  Back
                </button>
              </div>

              <div className={styles.practicesChildContent}>
                {activePractices.children.map(item => {
                  return (
                    <>
                      <button
                        key={item.title}
                        type="button"
                        className={cn(styles.categoryItemPractices, styles.categoryItemPracticesBold)}
                        onClick={() => handleSelectPracticeAndCloseModal(item)}
                      >
                        {item.title}
                      </button>

                      {item.children
                        ? item.children.map(itemChildren => (
                            <>
                              <button
                                key={itemChildren.title}
                                type="button"
                                className={styles.categoryItemPractices}
                                onClick={() => handleSelectPracticeAndCloseModal(itemChildren)}
                              >
                                {itemChildren.title}
                              </button>

                              {itemChildren.children
                                ? itemChildren.children.map(subChildren => (
                                    <>
                                      <button key={subChildren.title} type="button" className={styles.categoryItemPractices}>
                                        {subChildren.title}
                                      </button>
                                    </>
                                  ))
                                : null}
                            </>
                          ))
                        : null}
                    </>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

const CommunitiesModal = ({ closeModal, communities, handleSelectCommunities, activeCommunities, allCommunityValue }) => {
  // A group with no community (the "By Language" group once Deaf has moved out, GROWTH-7944) has nothing to pick: no tag, no filter option.
  const safeModalCommunities = useMemo(
    () => (Array.isArray(communities) ? communities.filter(group => group?.children?.length > 0) : EMPTY_COMMUNITIES),
    [communities]
  );
  const [activeCommunitiesType, setActiveCommunitiesType] = useState("");
  const filterLabelId = useId();
  const [activeCommunityChildren, setActiveCommunityChildren] = useState("");
  const [communitiesList, setCommunitiesList] = useState([]);
  const [search, setSearch] = useState("");

  const handleSetCommunities = name => {
    if (name === activeCommunitiesType) {
      setActiveCommunitiesType("");
    } else {
      setActiveCommunitiesType(name);
    }
  };

  const handleChange = event => {
    setSearch(event.target.value);
  };

  useEffect(() => {
    if (activeCommunities?.heritage_title && !activeCommunityChildren.heritage_title) {
      setActiveCommunityChildren(activeCommunities);
    }
  }, []);

  useEffect(() => {
    setCommunitiesList(safeModalCommunities);
  }, []);

  const normalizeCommunitiesList = useMemo(() => {
    const seen = new Set();
    const updateCommunitiesList = safeModalCommunities
      .reduce((acc, community) => {
        const children = (community.children || []).map(itemChildren => ({
          ...itemChildren,
          name: community.heritage_group_title
        }));
        return [...acc, ...children];
      }, [])
      .filter(item => {
        const title = item.heritage_title.trim().toLowerCase();
        if (seen.has(title)) return false;
        seen.add(title);
        return true;
      })
      .sort((a, b) => a.heritage_title.localeCompare(b.heritage_title));

    return activeCommunitiesType
      ? updateCommunitiesList.filter(item => item.name.toLowerCase() === activeCommunitiesType)
      : updateCommunitiesList;
  }, [safeModalCommunities, activeCommunitiesType]);

  useEffect(() => {
    if (search) {
      const searchCommunities = normalizeCommunitiesList.filter(communityItem => {
        return communityItem.heritage_title.toLowerCase().includes(search.toLowerCase());
      });

      setCommunitiesList(searchCommunities);
    } else {
      setCommunitiesList(normalizeCommunitiesList);
    }
  }, [search, normalizeCommunitiesList.length]);

  const handleClickViewAll = () => {
    handleSelectCommunities({ heritage_title: allCommunityValue.heritage_title, heritagePath: allCommunityValue.heritage_path });
  };

  return (
    <Modal closeModal={closeModal} clearAllModals={closeModal} classNameMain={styles.modal}>
      <div className={styles.heading}>
        <h6 className={styles.title}>All Communities</h6>
      </div>

      {safeModalCommunities.length ? (
        <>
          <div className={styles.filterMobile}>
            <span id={filterLabelId} className={styles.label}>
              Filter by:
            </span>
            <SelectInput
              tabSelectsValue={false}
              classNameWrap={styles.selectWrapper}
              className={styles.select}
              ariaLabelledBy={filterLabelId}
              name="country"
              placeholder="Filter By"
              onChange={value => handleSetCommunities(value.label.toLowerCase())}
              options={safeModalCommunities.map(item => ({ label: item.heritage_group_title, value: item.heritage_group_id }))}
              value={activeCommunitiesType ? { label: activeCommunitiesType } : ""}
            />
          </div>

          <div className={styles.flexContent}>
            <div className={styles.tags}>
              {safeModalCommunities.map(item => {
                return (
                  <button
                    key={`${item.heritage_group_title}-${item.heritage_path}`}
                    type="button"
                    className={cn(
                      styles.tagItem,
                      item.heritage_group_title.toLowerCase() === activeCommunitiesType ? styles.tagItemActive : ""
                    )}
                    onClick={() => handleSetCommunities(item.heritage_group_title.toLowerCase())}
                  >
                    {item.heritage_group_title}
                  </button>
                );
              })}
            </div>

            <button type="button" className={cn(styles.viewAll, styles.viewAllDesktop)} onClick={handleClickViewAll}>
              View All
            </button>
          </div>
        </>
      ) : null}

      <div className={cn(styles.inputWrap, styles.inputWrapModal)}>
        <SearchIcon className={styles.searchIcon} />
        <input type="text" className={cn(styles.input, styles.inputModal)} placeholder="Search" onChange={handleChange} value={search} />
      </div>

      <div className={styles.categories}>
        {[...new Map(communitiesList.map(item => [item.heritage_title?.trim().toLowerCase(), item])).values()].map(item => {
          const isActive = activeCommunityChildren.heritage_title === item.heritage_title;

          return (
            <button
              key={`${item.heritage_title}-${item.heritage_path}-${item.name}`}
              type="button"
              className={cn(styles.categoryItem, isActive ? styles.categoryItemActive : "")}
              onClick={() => handleSelectCommunities(item)}
            >
              {item.heritage_title}
            </button>
          );
        })}
      </div>
    </Modal>
  );
};

export default MainSearch;
